<template>
  <div class="setup-fee-market-form" data-testid="setup-fee-market-form">
    <div class="form-fields">
      <!-- Currency: selectable in add mode, static in edit mode (the row's
           identity is (currency, provider) — changing it would be a new row) -->
      <select-list
        v-if="!isEdit"
        v-model="formData.currency"
        elem-id="setup-fee-market-currency"
        :label="t('Moeda')"
        :itens-select="currencyOptions"
        :auto-select-first="true"
        data-testid="setup-fee-market-currency-select"
      />
      <div v-else class="field-static">
        <label class="field-label">{{ t('Moeda') }}</label>
        <span class="field-value-static" data-testid="setup-fee-market-currency-static">
          {{ formData.currency }}
        </span>
      </div>

      <select-list
        v-model="formData.provider_code"
        elem-id="setup-fee-market-gateway"
        :label="t('Gateway')"
        :itens-select="gatewayOptions"
        :auto-select-first="true"
        data-testid="setup-fee-market-gateway-select"
      />

      <input-currency
        v-model="priceMajor"
        elem-id="setup-fee-market-price"
        :label="t('Valor sugerido')"
        :currency="formData.currency"
        :currency-prefix="currencyLocale"
        data-testid="setup-fee-market-price-input"
      />

      <input-number
        v-model="formData.max_installments"
        elem-id="setup-fee-market-installments"
        :label="t('Máximo de parcelas')"
        :min="1"
        :max="24"
        data-testid="setup-fee-market-installments-input"
      />

      <select-list-multiple
        v-model="formData.countries"
        elem-id="setup-fee-market-countries"
        :label="t('Países')"
        :itens-select="countryOptions"
        data-testid="setup-fee-market-countries-select"
      />
    </div>

    <!--
      Per-country installment caps. One number on the record cannot serve every
      country it covers: this same BRL/asaas record prices Brazil and Chile, and
      ASAAS forbids parcelamento for a foreign payer.
    -->
    <div v-if="formData.countries.length > 0" class="country-caps" data-testid="setup-fee-market-country-caps">
      <p class="country-caps__hint">
        {{ t('Máximo de parcelas por país (vazio = usa o máximo do mercado)') }}
      </p>
      <div
        v-for="country in formData.countries"
        :key="country"
        class="country-caps__row"
      >
        <input-number
          v-model="countryCaps[country]"
          :elem-id="`setup-fee-market-installments-${country}`"
          :label="country"
          :min="1"
          :max="formData.max_installments"
          :data-testid="`setup-fee-market-installments-${country}-input`"
        />
        <!--
          The gateway may narrow further than staff can. Showing the resulting
          number next to the input is the only way to tell "I set 1" apart from
          "ASAAS allows only 1" — otherwise a staff member types 12, saves, sees
          1 come back, and cannot tell whether the setting failed.
        -->
        <span
          v-if="gatewayCapFor(country) !== null"
          class="country-caps__ceiling"
          :data-testid="`setup-fee-market-ceiling-${country}`"
        >
          {{ t('limite do gateway') }}: {{ gatewayCapFor(country) }}
        </span>
      </div>
    </div>

    <div v-if="errorMessage" class="error-message" data-testid="setup-fee-market-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { log } from '../../../utils/DebugLogger'
import { useSetupFee } from '../../../composables/useSetupFee'
import { usePaymentGatewayRegistry } from '../../../composables/usePaymentGatewayRegistry'
import { useMarketCountries } from '../../../composables/useMarketCountries'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import { emitValidationFailed } from '../../../composables/useFormValidation'
import SelectList from '../../../inputs/SelectList.vue'
import SelectListMultiple from '../../../inputs/SelectListMultiple.vue'
import InputCurrency from '../../../inputs/InputCurrency.vue'
import InputNumber from '../../../inputs/InputNumber.vue'
import type { SetupFee_DefaultRow } from '../../../contracts/SetupFee_DefaultRow'

const { t } = useI18n()
const { saveDefault } = useSetupFee()
const { featureMap, loadMap, currenciesForProvider } = usePaymentGatewayRegistry()
const { marketCurrencies, load: loadMarketCountries, countriesForCurrency } = useMarketCountries()

const props = defineProps<{
  /** Present = edit mode (currency/provider fixed); absent = add mode. */
  market?: SetupFee_DefaultRow | null
}>()

const emit = defineEmits<{
  (e: 'saved'): void
  (e: 'validation-failed'): void
}>()

const CURRENCY_LOCALE: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  CLP: 'es-CL',
}

const isEdit = computed(() => props.market != null)
const isSaving = ref(false)
const errorMessage = ref<string>('')
const priceMajor = ref<number>(1500)

const formData = reactive({
  currency: '',
  provider_code: '',
  max_installments: 12,
  countries: [] as string[],
  active: true,
})

const currencyLocale = computed(() => CURRENCY_LOCALE[formData.currency] ?? 'pt-BR')

const currencyOptions = computed(() =>
  marketCurrencies.value.map((c) => ({ ID: c, Name: c })),
)

/** Gateways whose static capability matrix supports this currency. */
const gatewayOptions = computed(() => {
  if (!formData.currency || !featureMap.value) return []
  const providers = new Set<string>()
  for (const map of Object.values(featureMap.value)) {
    for (const code of Object.keys(map)) {
      const supported = currenciesForProvider(code)
      if (supported.includes('*') || supported.includes(formData.currency)) {
        providers.add(code)
      }
    }
  }
  return Array.from(providers).map((p) => ({ ID: p, Name: p }))
})

const countryOptions = computed(() => countriesForCurrency(formData.currency))

/**
 * Per-country commercial caps being edited: ISO2 => number, or undefined to
 * inherit the record's `max_installments`.
 *
 * Kept OUTSIDE formData because it is sparse by design — an absent key means
 * "inherit", which is a different statement from any number staff could type,
 * and folding it into formData would force a value for every country.
 */
const countryCaps = reactive<Record<string, number | undefined>>({})

/**
 * The gateway's own ceiling for a country on the CURRENTLY selected record, or
 * null when it imposes none.
 *
 * Derived by comparing the effective cap the backend computed against the
 * record cap it was computed from — never by re-implementing the provider rule
 * here. The server owns that rule; the form only reports what it decided.
 */
function gatewayCapFor(country: string): number | null {
  const effective = editingRow.value?.max_installments_by_country?.[country]
  if (typeof effective !== 'number') return null
  const configured = editingRow.value?.configured_max_installments_by_country?.[country]
  const commercial = typeof configured === 'number' ? configured : editingRow.value?.max_installments
  return typeof commercial === 'number' && effective < commercial ? effective : null
}

// Currency change (add mode): drop a gateway that cannot charge it, and offer
// every country of the new currency as the starting selection.
watch(
  () => formData.currency,
  (newCurrency, oldCurrency) => {
    if (!newCurrency || isEdit.value) return
    const valid = gatewayOptions.value.map((g) => g.ID)
    if (valid.length > 0 && !valid.includes(formData.provider_code)) {
      formData.provider_code = valid[0]
    }
    if (oldCurrency !== newCurrency) {
      formData.countries = countryOptions.value.map((c) => c.ID)
    }
  },
)

/** The record being edited — the source for the gateway-ceiling display. */
const editingRow = ref<SetupFee_DefaultRow | null>(null)

function populate(market: SetupFee_DefaultRow): void {
  editingRow.value = market
  formData.currency = market.currency
  formData.provider_code = market.provider_code
  formData.max_installments = market.max_installments
  formData.countries = Array.isArray(market.countries) ? [...(market.countries as string[])] : []
  formData.active = market.active !== false
  const factor = currencyFactor(currencyLocale.value, market.currency)
  priceMajor.value = market.amount_cents / factor

  // CONFIGURED, not effective. Loading the effective cap would overwrite the
  // staff setting with whatever the gateway narrowed it to, and the next save
  // would persist that narrowing as if a human had chosen it.
  for (const key of Object.keys(countryCaps)) delete countryCaps[key]
  const configured = market.configured_max_installments_by_country ?? {}
  for (const [country, cap] of Object.entries(configured)) {
    if (typeof cap === 'number' && cap > 0) countryCaps[country] = cap
  }
}

function validateForm(): boolean {
  errorMessage.value = ''
  if (!formData.currency) {
    errorMessage.value = t('Selecione uma moeda')
    emitValidationFailed('SetupFeeMarketForm', { currency: errorMessage.value })
    return false
  }
  if (!formData.provider_code) {
    errorMessage.value = t('Selecione um gateway')
    emitValidationFailed('SetupFeeMarketForm', { provider_code: errorMessage.value })
    return false
  }
  if (!priceMajor.value || priceMajor.value <= 0) {
    errorMessage.value = t('Informe um valor válido')
    emitValidationFailed('SetupFeeMarketForm', { amount: errorMessage.value })
    return false
  }
  if (!formData.countries || formData.countries.length === 0) {
    errorMessage.value = t('Selecione pelo menos um país')
    emitValidationFailed('SetupFeeMarketForm', { countries: errorMessage.value })
    return false
  }
  return true
}

async function handleSubmit(): Promise<boolean> {
  if (!validateForm()) {
    emit('validation-failed')
    return false
  }
  isSaving.value = true
  try {
    const factor = currencyFactor(currencyLocale.value, formData.currency)
    await saveDefault({
      currency: formData.currency,
      provider_code: formData.provider_code,
      amount_cents: Math.round(priceMajor.value * factor),
      max_installments: formData.max_installments,
      countries: [...formData.countries],
      // Only countries still selected, and only real numbers: an entry for a
      // country the record no longer serves is REFUSED by the backend contract
      // (country_cap_fora_do_mercado), and a stale key here would block a save
      // over a field the form has already stopped showing.
      max_installments_by_country: Object.fromEntries(
        formData.countries
          .filter((c) => typeof countryCaps[c] === 'number' && (countryCaps[c] as number) > 0)
          .map((c) => [c, countryCaps[c] as number]),
      ),
      active: formData.active,
    })
    emit('saved')
    return true
  } catch (err) {
    errorMessage.value = (err as Error)?.message || t('Erro ao salvar mercado de taxa de implantação')
    log('SetupFeeMarketForm', 'handleSubmit', 'Erro ao salvar mercado', err)
    return false
  } finally {
    isSaving.value = false
  }
}

onMounted(async () => {
  try {
    await Promise.all([loadMap(), loadMarketCountries()])
  } catch (err) {
    log('SetupFeeMarketForm', 'onMounted', 'Erro ao carregar dados de mercado', err)
  }
  if (props.market) {
    populate(props.market)
  } else if (formData.currency) {
    // auto-select-first may have set the currency before countries loaded.
    formData.countries = countryOptions.value.map((c) => c.ID)
  }
})

defineExpose({
  handleSubmit,
  validateForm,
  isSaving,
})
</script>

<style scoped>
.country-caps {
  margin-top: 1rem;
}

.country-caps__hint {
  margin: 0 0 0.5rem;
  font-size: 0.85rem;
  opacity: 0.75;
}

.country-caps__row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.country-caps__ceiling {
  font-size: 0.8rem;
  opacity: 0.75;
  white-space: nowrap;
}

.setup-fee-market-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.field-static {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.field-label {
  font-weight: 600;
  font-size: 0.875rem;
}
.field-value-static {
  padding: 0.4rem 0;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
}
</style>
